/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { Bars2Icon, XMarkIcon } from '@heroicons/react/20/solid'
import { Column } from '@tanstack/react-table'
import classNames from 'classnames'
import type { Identifier } from 'dnd-core'
import { useRef } from 'react'
import { useDrag, useDrop, XYCoord } from 'react-dnd'

interface DragItem {
  column: Column<any>
  index: number
}

export default function DraggableColumn({
  column,
  index,
  onDrop,
  onRemove,
}: {
  column: Column<any, unknown>
  index: number
  onDrop: (draggedColumnId: string, targetColumnId: string) => void
  onRemove: (columnId: string) => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  const [{ handlerId }, dropRef] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: `column`,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover: (item: DragItem, monitor) => {
      if (!ref.current) {
        return
      }
      if (column.columnDef.meta && column.columnDef.meta![`pinned`]) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      onDrop(item.column.id, column.id)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    canDrop(item, monitor) {
      return !column.columnDef.meta || !column.columnDef.meta![`pinned`]
    },
  })

  const [{ isDragging }, dragRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag(monitor) {
      return !column.columnDef.meta || !column.columnDef.meta![`pinned`]
    },
    item: () => ({ column, index }),
    type: `column`,
  })
  dragRef(dropRef(ref))
  return (
    <div
      data-handler-id={handlerId}
      ref={ref}
      className={classNames(
        `flex h-9 items-center justify-between rounded-md bg-surface px-2 shadow-elevation-1`,
        isDragging ? `opacity-0` : `opacity-100`,
        !column.columnDef.meta || !column.columnDef.meta![`pinned`] ? `cursor-move` : `cursor-default`,
      )}
    >
      <div className="flex items-center">
        <Bars2Icon
          className={classNames(
            `h-5 w-5 fill-current`,
            !column.columnDef.meta || !column.columnDef.meta![`pinned`] ? `text-default` : `text-minimal`,
          )}
        />
        <div aria-label={column.columnDef.header?.toString()} className="ml-2 text-body-sm text-default">
          {column.columnDef.header?.toString()}
        </div>
      </div>
      {(!column.columnDef.meta || !column.columnDef.meta![`pinned`]) && (
        <XMarkIcon onClick={() => onRemove(column.id)} className="h-5 w-5 cursor-pointer fill-current text-default" />
      )}
    </div>
  )
}
